import React, { useEffect, useState } from 'react';

import { ActionIcon, LoadingOverlay, Stack, Text, Title, createStyles } from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import { UseQueryResult } from 'react-query';
import { PackageRetrieve } from 'types';

import BillingPlanForm from './components/BillingPlanForm/BillingPlanForm';
import TaxRateForm from './components/TaxRateForm';
import PackageDrawerState from '../../types';

const useStyles = createStyles((theme, { isDisabled }: { isDisabled: boolean }) => ({
  panel: {
    background: isDisabled ? theme.colors.gray[1] : undefined,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'scroll',
  },
  content: {
    flexGrow: 1,
    overflowY: 'scroll',
  },
}));

interface IBillingPanelProps {
  state: PackageDrawerState;
  packageHook: UseQueryResult<{
    data: PackageRetrieve;
  }>;
  packageId: string;
  permission?: boolean;
}

const BillingPanel = ({
  state,
  packageHook,
  packageId,
  permission = true,
}: IBillingPanelProps): JSX.Element => {
  const isDisabled = state !== 'create-billing' && state !== 'edit-billing';
  const { classes } = useStyles({ isDisabled });

  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    setShowForm(false);
  }, [state]);

  return (
    <div className={classes.panel}>
      <LoadingOverlay visible={packageHook.isLoading} />
      <Stack px='lg' py='md' spacing='lg'>
        <Stack spacing={0}>
          <Title order={4}>Billing</Title>
          <Text size='sm' color='dimmed'>
            Create and manage billing plans for this Package.
          </Text>
        </Stack>
        <TaxRateForm state={state} packageHook={packageHook} packageId={packageId} />
        <Stack className={classes.content} data-cy='billingPanel'>
          {packageHook.data?.data.billingPlans &&
            packageHook.data.data.billingPlans.map(bp => (
              <BillingPlanForm
                key={bp.billingPlanId}
                state={state}
                packageData={packageHook.data.data}
                billingPlan={bp}
                isDisabled={isDisabled}
                permission={permission}
                data-cy='billingPlanForms'
              />
            ))}
          {showForm && packageHook.data?.data && (
            <BillingPlanForm
              state={state}
              packageData={packageHook.data.data}
              isDisabled={isDisabled}
              permission={permission}
              handleCloseForm={() => setShowForm(false)}
            />
          )}
          {!isDisabled && (
            <ActionIcon
              data-cy='addBillingPlanIcon'
              variant='transparent'
              color='kilo-blue'
              onClick={() => setShowForm(true)}
              disabled={showForm || !permission}
            >
              <IconPlus />
            </ActionIcon>
          )}
        </Stack>
      </Stack>
    </div>
  );
};

export default BillingPanel;
